/**
 * `Campanha · Assistente IA` (Figma `LrdQopN1jNWKJFWxOkn0IY` node `360:14197`):
 * the conversation over AS-3's client, plus the empty `· início` state
 * (`Start.tsx`) when nothing has been asked yet. Unmounted here -- the route
 * segment and the nav entry's mount point are AS-5's call (plan issue 9).
 *
 * A turn's follow-up chips (`FollowUps.tsx`) are drawn from the stream's own
 * `followups` event, quoting the answer just given -- `Start`'s four fixed
 * suggestions remain the only invented copy, for before any turn exists.
 */

import {
  Activity,
  ArrowDown,
  ArrowUpRight,
  Calendar,
  Image as ImageIcon,
  Megaphone,
  SquarePen,
} from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { ConfirmDialog } from '../../../chrome/form/ConfirmDialog';
import { IconHint } from '../../../chrome/hint';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import { IconButton } from '../../../ui/icon-button';
import { Textarea } from '../../../ui/textarea';
import { cardFor, isChartResult, type AssistantCardContext } from './cards';
import { FollowUps } from './FollowUps';
import { AnswerText } from './AnswerText';
import { Interrupted } from './Interrupted';
import { at } from './messages';
import { Refusal } from './Refusal';
import type { RefusalData, ResultData, TurnWindow } from './schemas';
import { Start } from './Start';
import { useConversation, type TurnRecord, type UseConversationArgs } from './useConversation';

export interface PostScope {
  id: string;
  /** The post's caption first line, or its handle when there is no caption --
   *  whichever the reader would recognise the post by. */
  label: string;
}

export interface AssistantScope {
  /** Already-formatted display labels -- the window selection is not this
   *  task's job (plan issue 9), so the caller supplies it. The account set
   *  is not a scope dimension: nothing here filters by it, so nothing here
   *  labels it. */
  campaignLabel: string;
  windowLabel: string;
  window: TurnWindow | null;
  /** The post named by the URL's `?post=`, once it resolved -- absent with no
   *  `?post=` and while `unknownPost` is true. */
  post?: PostScope | null;
  /** `?post=` named an id this organisation's collection has no post for: the
   *  assistant still opens unscoped rather than failing the whole screen. */
  unknownPost?: boolean;
}

/** A line's Markdown -- heading marks, a list marker, a quote, bold, code -- dropped, as the
 *  api drops it when it turns a suggested question into a chip. */
function plainLine(line: string): string {
  return line
    .replace(/^\s*(#+|[-*>]|\d+[.)])\s+/, '')
    .replace(/\*\*|`/g, '')
    .trim();
}

/** The answer without the suggested questions it closed on, once those arrive as chips:
 *  the reader sees each question once, as the chip that asks it. */
function textOf(turn: TurnRecord): string {
  const text = turn.events
    .filter((event): event is Extract<typeof event, { kind: 'delta' }> => event.kind === 'delta')
    .map((event) => event.data.text)
    .join('');
  const chips = new Set(
    turn.events.flatMap((event) => (event.kind === 'followups' ? event.data.chips : [])),
  );
  if (chips.size === 0) return text;
  return text
    .split('\n')
    .filter((line) => !chips.has(plainLine(line)))
    .join('\n')
    .trimEnd();
}

function resultsOf(turn: TurnRecord): ResultData[] {
  return turn.events
    .filter((event): event is Extract<typeof event, { kind: 'result' }> => event.kind === 'result')
    .map((event) => event.data);
}

function refusalOf(turn: TurnRecord): RefusalData | null {
  const event = turn.events.find((candidate) => candidate.kind === 'refusal');
  return event === undefined ? null : event.data;
}

function errorOf(turn: TurnRecord): string | undefined {
  const event = turn.events.find((candidate) => candidate.kind === 'error');
  return event?.data.message;
}

function ChatBubble(props: { question: string }): ReactNode {
  return (
    <div className="flex w-full max-w-3xl justify-end">
      <p className="max-w-xl rounded-xl bg-primary-quiet px-4 py-3 text-body text-primary-ink break-words">
        {props.question}
      </p>
    </div>
  );
}

function Answer(props: {
  turn: TurnRecord;
  cardContext: AssistantCardContext;
  onAskAgain: () => void;
  onAsk: (question: string) => void;
}): ReactNode {
  const { turn } = props;
  const text = textOf(turn);
  const refusal = refusalOf(turn);
  const results = resultsOf(turn);
  const charts = results
    .filter((result) => isChartResult(result.name))
    .map((result) => cardFor(result, props.cardContext))
    .filter((card) => card !== null);
  const cards = results
    .filter((result) => !isChartResult(result.name))
    .map((result) => cardFor(result, props.cardContext))
    .filter((card) => card !== null);
  const thinking = turn.status === 'streaming' && text === '' && refusal === null;

  return (
    <div className="flex w-full max-w-3xl flex-col items-start gap-3">
      <div className="flex items-center gap-2">
        <div className="flex size-6 shrink-0 items-center justify-center rounded-pill bg-primary">
          <Activity aria-hidden className="size-3.5 text-primary-foreground" />
        </div>
        <span className="text-label-sm text-muted-foreground">{at('author.name')}</span>
      </div>
      {thinking ? (
        <span role="status" className="text-body text-muted-foreground">
          {at('answer.thinking')}
        </span>
      ) : (
        text !== '' && <AnswerText text={text} />
      )}
      {refusal !== null && <Refusal refusal={refusal} />}
      {charts.map((chart, index) => (
        <div key={index} className="w-full">
          {chart}
        </div>
      ))}
      {cards.length > 0 && (
        // The answer is the analysis; the readings behind it are evidence a reader opens on
        // demand, never a list shown in its place.
        <details className="w-full" open={text === '' && refusal === null}>
          <summary className="cursor-pointer text-label-sm text-muted-foreground">
            {at('evidence.show', { count: String(cards.length) })}
          </summary>
          <div className="mt-3 flex flex-col gap-3">
            {cards.map((card, index) => (
              <div key={index} className="w-full">
                {card}
              </div>
            ))}
          </div>
        </details>
      )}
      {turn.status === 'interrupted' && (
        <Interrupted onAskAgain={props.onAskAgain} message={errorOf(turn)} />
      )}
      <FollowUps turn={turn} onAsk={props.onAsk} />
    </div>
  );
}

/** How close to the bottom, in px, still counts as reading the latest answer. */
const FOLLOW_SLACK = 80;

/** Keeps the list pinned to the newest content while the reader is at the
 *  bottom, and lets go the moment they scroll up. */
function useFollowLatest(turnCount: number) {
  const scroller = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const lastTop = useRef(0);
  // The start screen reads from its greeting down, so nothing follows there.
  const hasTurns = useRef(turnCount > 0);
  hasTurns.current = turnCount > 0;
  const [awayFromLatest, setAwayFromLatest] = useState(false);

  function toLatest(behavior: ScrollBehavior = 'auto'): void {
    const node = scroller.current;
    if (node === null) return;
    following.current = true;
    if (behavior === 'auto') node.scrollTop = node.scrollHeight;
    else node.scrollTo({ top: node.scrollHeight, behavior });
  }

  function onScroll(): void {
    const node = scroller.current;
    if (node === null) return;
    const near = node.scrollHeight - node.scrollTop - node.clientHeight <= FOLLOW_SLACK;
    // Only an upward scroll lets go: a smooth jump and a growing answer both
    // move the view down, and neither is the reader leaving.
    if (near) following.current = true;
    else if (node.scrollTop < lastTop.current) following.current = false;
    lastTop.current = node.scrollTop;
    setAwayFromLatest(!near);
  }

  // A question just sent comes into view wherever the reader had scrolled.
  useLayoutEffect(() => {
    if (turnCount > 0) toLatest();
  }, [turnCount]);

  useEffect(() => {
    const node = content.current;
    if (node === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (following.current && hasTurns.current) toLatest();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { scroller, content, onScroll, awayFromLatest, toLatest };
}

function NewConversation(props: { onReset: () => void }): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      title={at('reset.title')}
      description={at('reset.description')}
      confirmLabel={at('reset.confirm')}
      pending={false}
      failure={null}
      destructive
      onConfirm={() => {
        props.onReset();
        setOpen(false);
      }}
      trigger={
        <Button type="button" variant="outline" size="sm">
          <SquarePen aria-hidden />
          {at('reset.trigger')}
        </Button>
      }
    />
  );
}

function Composer(props: {
  scope: AssistantScope;
  disabled: boolean;
  onSend: (question: string) => void;
  onClearPost?: () => void;
}): ReactNode {
  const [value, setValue] = useState('');
  const input = useRef<HTMLTextAreaElement>(null);

  function submit(): void {
    const question = value.trim();
    if (question === '' || props.disabled) return;
    props.onSend(question);
    setValue('');
    input.current?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  }

  return (
    <div className="flex w-full flex-col items-center">
      {/* The card carries the focus ring for the textarea inside it, which draws
          none of its own: one outline around the whole composer, as the frame
          draws it, rather than a second one nested inside. */}
      <div className="flex w-full max-w-3xl flex-col items-start gap-2 rounded-xl border border-input bg-card py-3 pr-3 pl-4 transition-shadow focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50">
        {/* Left typeable while an answer streams, only sending waits: a
            disabled textarea drops focus, and the next question is usually
            drafted while the last answer is still arriving. */}
        <Textarea
          ref={input}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={at('composer.placeholder')}
          rows={1}
          className="max-h-48 min-h-0 resize-none overflow-y-auto rounded-none border-none bg-transparent p-0 text-body shadow-none focus-visible:border-none focus-visible:ring-0 dark:bg-transparent"
        />
        <div className="flex w-full flex-wrap items-center gap-1.5">
          <Chip icon={<Megaphone aria-hidden />}>{props.scope.campaignLabel}</Chip>
          <Chip icon={<Calendar aria-hidden />}>{props.scope.windowLabel}</Chip>
          {props.scope.post != null &&
            (props.onClearPost ? (
              <Chip
                icon={<ImageIcon aria-hidden />}
                onClose={props.onClearPost}
                closeLabel={at('scope.post.clear')}
              >
                {props.scope.post.label}
              </Chip>
            ) : (
              <Chip icon={<ImageIcon aria-hidden />}>{props.scope.post.label}</Chip>
            ))}
          <div className="flex-1" />
          <IconHint label={at('composer.send')}>
            <IconButton
              type="button"
              tone="primary"
              label={at('composer.send')}
              disabled={props.disabled || value.trim() === ''}
              onClick={submit}
            >
              <ArrowUpRight aria-hidden />
            </IconButton>
          </IconHint>
        </div>
      </div>
    </div>
  );
}

export function Assistant(props: {
  campaignId: string;
  tenantId: string;
  scope: AssistantScope;
  /** Overridden in tests; see `useConversation`'s own `openStream`. */
  openStream?: UseConversationArgs['openStream'];
  /** Removes `?post=` from the URL; absent while there is no post chip to clear. */
  onClearPost?: () => void;
}): ReactNode {
  const { turns, busy, ask, askAgain, reset } = useConversation({
    campaignId: props.campaignId,
    tenantId: props.tenantId,
    window: props.scope.window,
    postId: props.scope.post?.id ?? null,
    openStream: props.openStream,
  });
  const cardContext: AssistantCardContext = {
    campaignId: props.campaignId,
    windowLabel: props.scope.windowLabel,
  };

  const follow = useFollowLatest(turns.length);

  return (
    // The shell's content box pads 1.5rem above and below and scrolls the
    // page; this cancels both so the column spans exactly the viewport under
    // the 3.5rem topbar, leaving the message list as the only scroller.
    <div className="-my-6 flex h-screen-minus-header w-full flex-col">
      {/* Hidden rather than disabled with no turns: the start screen has
          nothing to clear, and its frame draws no header row. */}
      {turns.length > 0 && (
        <div className="mx-auto flex w-full max-w-3xl shrink-0 justify-end pt-3">
          <NewConversation onReset={reset} />
        </div>
      )}
      {props.scope.unknownPost && (
        <Alert className="mx-auto mt-3 w-full max-w-3xl shrink-0">
          <AlertDescription>{at('scope.post.unknown')}</AlertDescription>
        </Alert>
      )}
      <div
        ref={follow.scroller}
        onScroll={follow.onScroll}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        <div ref={follow.content} className="flex w-full flex-1 flex-col">
          {turns.length === 0 ? (
            <Start campaignLabel={props.scope.campaignLabel} onAsk={ask} />
          ) : (
            <div className="flex w-full flex-1 flex-col items-center gap-6 py-6">
              {turns.map((turn) => (
                <div key={turn.id} className="flex w-full flex-col items-center gap-6">
                  <ChatBubble question={turn.question} />
                  <Answer
                    turn={turn}
                    cardContext={cardContext}
                    onAskAgain={() => askAgain(turn.id)}
                    onAsk={ask}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Sticky rather than absolute: it keeps its own height in the flow,
            so the last message always ends above it without measuring. */}
        <div className="sticky bottom-0 z-10 flex w-full flex-col items-center bg-linear-to-t from-background from-70% to-transparent pt-8 pb-safe-bottom">
          {follow.awayFromLatest && turns.length > 0 && (
            <IconHint label={at('scroll.latest')}>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => follow.toLatest('smooth')}
                className="absolute -top-8 rounded-pill bg-background shadow-md dark:bg-background"
              >
                <ArrowDown aria-hidden />
              </Button>
            </IconHint>
          )}
          <Composer
            scope={props.scope}
            disabled={busy}
            onSend={ask}
            onClearPost={props.onClearPost}
          />
        </div>
      </div>
    </div>
  );
}
